import { profileSelector } from '@common/redux/selectors/auth';
import { pagesSelector } from '@common/redux/selectors/page';
import { checkAuthError, checkAuthSuccess } from '@common/redux/slice/auth';
import { useHistory, useLocation } from '@common/routes/hooks';
import { IAppInfo, IPage } from '@common/types';
import { getMetadataScreen } from '@common/utils';
import { some } from 'lodash';
import qs from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EmitterSubscription, Linking, Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

type Props = {
  appInfo: IAppInfo;
};

type SearchQuery = {
  target: string;
  transition: string;
  targetModal: string;
  modalTransition: string;
};

let eventDeepLinkUrl: EmitterSubscription;
export const authenticationRouting = ({ appInfo }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const pages = useSelector(pagesSelector);
  const profile = useSelector(profileSelector);

  const { search } = useLocation();
  const query = useMemo(() => qs.parse(search) as SearchQuery, [search]);

  const [currentPageId, setCurrentPageId] = useState<string>();
  const [currentModalId, setCurrentModalId] = useState<string>();
  const [animation, setAnimation] = useState<string>('TRANSITION_PUSH');
  const [modalTransition, setModalTransition] =
    useState<string>('TRANSITION_PUSH');

  const handleUrlDeepLink = (event: { url: string | null }) => {
    if (event?.url && Platform.OS !== 'web') {
      const parsed = qs.parse(event?.url.split('?')[1]);
      const screenId = parsed?.screen;
      const streamId = parsed?.streamId;

      if (streamId && screenId) {
        history.replace({
          search: qs.stringify({
            ...query,
            ...{
              target: checkValidScreen(screenId),
            },
          }),
          state: { ...parsed },
        });
      }
    }
  };

  const findScreen = useCallback(
    (screenId: string) => {
      if (!screenId) return;
      const currentPage = pages[screenId];

      if (currentPage) {
        const cloneCurrent = JSON.parse(JSON.stringify(currentPage));
        cloneCurrent.metadata = getMetadataScreen(cloneCurrent);
        return cloneCurrent;
      }
      return undefined;
    },
    [pages]
  );

  const [loading, setLoading] = useState(false);

  const authenticationCheck = () => {
    setLoading(true);
    const { active, startHomePage } = appInfo.metadata;

    let id: string | string[];
    const { target } = query;
    if (target && target === checkValidScreen(target)) {
      setLoading(false);
      return;
    }
    if (profile?.id) {
      dispatch(checkAuthSuccess(profile));
      id = active;
    } else {
      dispatch(checkAuthError());
      id = startHomePage;
    }
    setLoading(false);

    history.replace({
      search: qs.stringify({
        ...query,
        ...{
          target: checkValidScreen(id),
        },
      }),
    });
  };

  const checkValidScreen = useCallback(
    (screenUuid: string | any) => {
      const isValid = !isEmpty(pages[screenUuid]);

      if (isValid) return screenUuid;
      else {
        return pages[Object.keys(pages)[0]]?.screenUuid;
      }
    },
    [pages]
  );

  useEffect(() => {
    authenticationCheck();
  }, []);

  useEffect(() => {
    if (profile?.id) {
      Linking.getInitialURL().then((url) => {
        return handleUrlDeepLink({
          url,
        });
      });

      eventDeepLinkUrl = Linking.addEventListener('url', (e) => {
        return handleUrlDeepLink(e);
      });
    }
    return () => {
      if (eventDeepLinkUrl) {
        eventDeepLinkUrl.remove();
      }
    };
  }, [profile]);

  useEffect(() => {
    const {
      target,
      transition,
      targetModal,
      modalTransition: queryModalTransition,
    } = query;

    setCurrentPageId((prev) => {
      if (prev !== target) {
        return target;
      }
      return prev;
    });
    setCurrentModalId((prev) => {
      if (prev !== targetModal) {
        return targetModal;
      }
      return prev;
    });
    setAnimation((prev) => {
      if (prev !== transition) {
        return transition;
      }
      return prev;
    });
    setModalTransition((prev) => {
      if (prev !== queryModalTransition) {
        return queryModalTransition;
      }
      return prev;
    });

    return;
  }, [query]);

  const currentPage: IPage | undefined = useMemo(() => {
    if (currentPageId) {
      return findScreen(currentPageId.toString());
    }
  }, [findScreen, currentPageId]);

  const currentModalPage: IPage | undefined = useMemo(() => {
    if (currentModalId) {
      return findScreen(currentModalId.toString());
    }
  }, [findScreen, currentModalId]);

  return {
    currentPageId,
    currentModalId,
    animation,
    modalTransition,
    screen: currentPage,
    modalScreen: currentModalPage,
    appInfo,
    loading,
  };
};
